// config/analyticsConfig.js
import { green, red, orange, blue, cyan, deepOrange, brown, teal } from "@mui/material/colors";
import { FUNCTIONALITY } from '../../../../constants/types';

export interface AnalyticsTab {
  id: string;
  label: string;
  color: string;
  enabled: boolean;
  /** @deprecated Prefer `functionalities` — kept for the Clinical tab (SKIN-35). */
  functionality?: FUNCTIONALITY;
  functionalities?: FUNCTIONALITY[];
}

const analyticsTabsList: AnalyticsTab[] = [
  {
    id: 'outpatients',
    label: 'Outpatients',
    color: blue[500],
    enabled: true,
    functionalities: [FUNCTIONALITY.OUTPATIENTS],
  },
  {
    id: 'inpatients',
    label: 'Inpatients',
    color: green[500],
    enabled: true,
    functionalities: [FUNCTIONALITY.HOSPITALIZATION],
  },
  {
    id: 'laboratory',
    label: 'Laboratory',
    color: orange[500],
    enabled: true,
    functionalities: [FUNCTIONALITY.REQUESTS],
  },
  {
    id: 'radiology',
    label: 'Radiology',
    color: red[500],
    enabled: true,
    functionalities: [FUNCTIONALITY.REQUESTS],
  },
  {
    id: 'finances',
    label: 'Finances',
    color: brown[500],
    enabled: true,
    functionalities: [FUNCTIONALITY.BILLING],
  },
  {
    // SKIN-35 — only shown in investigations with FUNCTIONALITY.CLINICAL (skincair).
    id: 'clinical',
    label: 'Clinical',
    color: teal[500],
    enabled: true,
    functionality: FUNCTIONALITY.CLINICAL,
  },
  {
    // SKIN-273 — cross-investigation comparison; same CLINICAL gate as Clinical tab.
    id: 'studies',
    label: 'Studies',
    color: cyan[700],
    enabled: true,
    functionality: FUNCTIONALITY.CLINICAL,
  },
  {
    id: 'staff',
    label: 'Staff',
    color: cyan[500],
    enabled: false,
  },
  {
    id: 'quality',
    label: 'Quality Indicators',
    color: deepOrange[500],
    enabled: false,
  }
];

export const analyticsTabs = analyticsTabsList.filter(tab => tab.enabled);

function tabRequiredFunctionalities(tab: AnalyticsTab): FUNCTIONALITY[] {
  if (tab.functionalities?.length) {
    return tab.functionalities;
  }
  if (tab.functionality) {
    return [tab.functionality];
  }
  return [];
}

function isTabVisibleForInvestigation(
  tab: AnalyticsTab,
  functionalities: FUNCTIONALITY[]
): boolean {
  const required = tabRequiredFunctionalities(tab);
  if (!required.length) {
    return true;
  }
  return required.some((functionality) => functionalities.includes(functionality));
}

// Tabs visible for a given investigation. Functionality-gated tabs only appear when
// the investigation has that functionality, matching sidebar route visibility.
export const getAnalyticsTabs = (investigation: { functionalities?: FUNCTIONALITY[] } | null | undefined) => {
  const functionalities = investigation?.functionalities || [];
  return analyticsTabs.filter((tab) => isTabVisibleForInvestigation(tab, functionalities));
};

export const analyticsConfig = {
  defaultColor: blue[500],
  activeColor: blue[700],
  subtitle: '',
  defaultTab: 'outpatients',
  scrollButtons: true,
  tabVariant: 'scrollable'
};
